import React from 'react';

import { Stack } from '@mui/material';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';

import { DARK_GREY } from '../../ThemeContext/constants';

import type { ReactNode } from 'react';

interface BugsnagClient {
  notify: (error: Error) => void;
}

// Bugsnag is loaded as a global browser client via `bugsnag_javascript_tags` (see
// ApplicationHelper), active only in the production/uat release stages. Reporting through it
// here classifies the error as "handled" rather than an unhandled crash.
const notifyBugsnag = (error: unknown) => {
  const client = (window as unknown as { Bugsnag?: BugsnagClient }).Bugsnag;
  if (client && typeof client.notify === 'function') {
    client.notify(error instanceof Error ? error : new Error(String(error)));
  }
};

const DefaultFallback = (
  <Stack sx={{ color: DARK_GREY, fontSize: '14px', padding: '16px 0px' }}>
    We couldn&apos;t display this content. Please refresh the page to try again.
  </Stack>
);

interface ComponentErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback UI. Defaults to a quiet inline message. */
  fallback?: ReactNode;
}

/**
 * Wraps a subtree so a runtime render/unmount exception recovers into a quiet inline fallback
 * instead of bubbling to a top-level (e.g. router) error screen.
 *
 * Motivating case (UIUX-3691): MUI v6 transitions (react-transition-group 4) race React 19's
 * reconciler on mount/unmount and can throw `Failed to execute 'removeChild' on 'Node'` while
 * e.g. the Activities tab is expanding/collapsing rows. Non-blocking, so contained here and
 * reported as handled instead of showing the global "Something went wrong!" boundary.
 */
const ComponentErrorBoundary = ({ children, fallback }: ComponentErrorBoundaryProps) => (
  <ReactErrorBoundary fallback={fallback ?? DefaultFallback} onError={notifyBugsnag}>
    {children}
  </ReactErrorBoundary>
);

export default ComponentErrorBoundary;
